import { createSignal, onMount } from "solid-js";
import type {
  EventName,
  EventProperties,
  UserProperties,
} from "../api/posthog";
import postHogClient from "../api/posthog";
import { devLog, devWarn } from "../libs/dev-log";

export function usePostHog() {
  const [isInitialized, setIsInitialized] = createSignal<boolean | undefined>(
    false,
  );
  const [isEnabled, setIsEnabled] = createSignal<boolean | undefined>(false);

  onMount(() => {
    setIsInitialized(postHogClient.getInitialized());
    setIsEnabled(postHogClient.getIsEnabled());
  });

  const isAlreadyIdentified = (id: string) => {
    return postHogClient.isIdentified(id);
  };

  const identify = (id: string, properties?: UserProperties) => {
    if (!isInitialized()) {
      devWarn("PostHog not initialized. Call init() first.");
      return;
    }
    if (!isEnabled()) {
      devWarn("Identify: PostHog is disabled.");
      return;
    }

    if (isAlreadyIdentified(id)) {
      devWarn("Already identified. Skipping...");
      return;
    }

    postHogClient.identify(id, properties);
  };

  const capture = (eventName: EventName, properties?: EventProperties) => {
    devLog("capture", eventName, properties, isInitialized(), isEnabled());
    if (!isInitialized()) {
      devWarn("PostHog not initialized. Call init() first.");
      return;
    }
    if (!isEnabled()) {
      devWarn("Capture: PostHog is disabled.");
      return;
    }
    postHogClient.capture(eventName, properties);
  };

  const captureException = (err: Error) => {
    if (!isInitialized()) {
      devWarn("PostHog not initialized. Call init() first.");
      return;
    }
    if (!isEnabled()) {
      devWarn("CaptureException: PostHog is disabled.");
      return;
    }
    postHogClient.captureException(err);
  };

  const reset = () => {
    if (!isInitialized()) {
      devWarn("PostHog not initialized. Call init() first.");
      return;
    }
    if (!isEnabled()) {
      devWarn("Reset: PostHog is disabled.");
      return;
    }
    postHogClient.reset();
  };

  return {
    identify,
    capture,
    captureException,
    isInitialized,
    isAlreadyIdentified,
    reset,
  };
}
